import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import Section from '../components/ui/Section';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import BookingForm from '../components/ui/booking-form';
import AnimatedSection from '../components/ui/AnimatedSection';
import SEOHelmet from '../seo/SEOHelmet';
import { useLanguage } from '../i18n/LanguageContext';
import HeaderBannerComponent from '../components/ui/HeaderBanner';
import { getAccommodations, Accommodation as AccommodationType } from '../utils/supabaseClient';
import Spinner from '../components/ui/Spinner';
import InteractiveMap, { Marker } from '../components/ui/InteractiveMap';
import PisaMap from '../components/ui/PisaMap';
import StaticPisaMap from '../components/ui/StaticPisaMap';
import useModal from '../utils/useModal';

// Styled components for this page
const AccommodationsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 2rem;
  margin-top: 2rem;
`;

const FiltersContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1.5rem;
  margin-bottom: 2rem;
  align-items: center;
  padding: 1.5rem;
  background: transparent;
  border-radius: 12px;
  box-shadow: none;
`;

const FilterHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  margin-bottom: 0.5rem;
`;

const FilterTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: white;
  margin: 0;
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const FilterLabel = styled.span`
  font-weight: 500;
  color: white;
  font-size: 0.9rem;
`;

const FilterButtonsGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const FilterSelect = styled.select`
  padding: 0.75rem 1rem;
  border: 1px solid white;
  border-radius: 8px;
  background-color: transparent;
  color: white;
  font-size: 0.9rem;
  min-width: 150px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  option {
    background-color: #222;
    color: white;
  }
  
  &:hover, &:focus {
    border-color: #004744;
    outline: none;
  }
`;

const FilterButton = styled.button<{ active: boolean }>`
  padding: 0.75rem 1.25rem;
  background-color: ${props => props.active ? '#004744' : 'transparent'};
  color: white;
  border: 1px solid white;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.9rem;
  
  &:hover {
    background-color: ${props => props.active ? '#004744' : 'rgba(255, 255, 255, 0.1)'};
    border-color: white;
  }
`;

const ClearFiltersButton = styled.button`
  padding: 0.75rem 1.25rem;
  background-color: transparent;
  color: white;
  border: none;
  cursor: pointer;
  text-decoration: none;
  font-size: 0.9rem;
  display: flex;
    align-items: center;
  gap: 0.5rem;
  
  &:hover {
    color: #004744;
  }
  
  &::before {
    content: "↺";
    font-size: 1rem;
  }
`;

const LayoutToggle = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-left: auto;
`;

const LayoutButton = styled.button<{ active: boolean }>`
  padding: 0.75rem;
  background-color: ${props => props.active ? '#004744' : 'transparent'};
  color: white;
  border: 1px solid ${props => props.active ? '#004744' : 'white'};
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: ${props => props.active ? '#004744' : 'rgba(255, 255, 255, 0.1)'};
    border-color: ${props => props.active ? '#004744' : '#004744'};
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(0, 0, 0, 0.8), rgba(0, 0, 0, 0.6));
  backdrop-filter: blur(8px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 1rem;
  animation: fadeIn 0.3s ease-out;
  
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

const ModalContent = styled.div`
  background: linear-gradient(135deg, #ffffff 0%, #f8fafb 100%);
  border-radius: 24px;
  max-width: 900px;
  width: 100%;
  max-height: 95vh;
  overflow-y: auto;
  position: relative;
  color: #2d3748;
  box-shadow: 
    0 25px 50px -12px rgba(0, 0, 0, 0.25),
    0 0 0 1px rgba(255, 255, 255, 0.8);
  animation: slideIn 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
  
  @keyframes slideIn {
    from { 
      opacity: 0;
      transform: translateY(20px) scale(0.95);
    }
    to { 
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }
  
  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.05);
    border-radius: 10px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: linear-gradient(135deg, #004744, #004744);
    border-radius: 10px;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1.5rem;
  right: 1.5rem;
  background: linear-gradient(135deg, #ffffff 0%, #f7fafc 100%);
  border: none;
  border-radius: 50%;
  width: 3rem;
  height: 3rem;
  font-size: 1.25rem;
  color: #4a5568;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  z-index: 110;
  box-shadow: 
    0 10px 25px rgba(0, 0, 0, 0.15),
    0 0 0 1px rgba(255, 255, 255, 0.9),
    inset 0 1px 0 rgba(255, 255, 255, 0.8);
  transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  
  &:hover {
    transform: scale(1.1) rotate(90deg);
    background: linear-gradient(135deg, #f56565 0%, #e53e3e 100%);
    color: white;
    box-shadow: 
      0 15px 35px rgba(229, 62, 62, 0.4),
      0 0 0 1px rgba(255, 255, 255, 0.9);
  }
`;

const ModalBody = styled.div`
  padding: 3rem;
  padding-top: 1rem;
`;

const ImageContainer = styled.div`
  position: relative;
  height: 450px;
  border-top-left-radius: 24px;
  border-top-right-radius: 24px;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 100px;
    background: linear-gradient(transparent, rgba(0, 0, 0, 0.3));
    z-index: 1;
    pointer-events: none;
  }
`;

const Image = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
  
  &:hover {
    transform: scale(1.02);
  }
`;

const ImageNav = styled.div`
  position: absolute;
  top: 50%;
  width: 100%;
  display: flex;
  justify-content: space-between;
  transform: translateY(-50%);
  padding: 0 1.5rem;
  z-index: 2;
`;

const NavButton = styled.button`
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(247, 250, 252, 0.9) 100%);
  border: none;
  border-radius: 50%;
  width: 3.5rem;
  height: 3.5rem;
  font-size: 1.25rem;
  color: #2d3748;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  backdrop-filter: blur(10px);
  box-shadow: 
    0 8px 25px rgba(0, 0, 0, 0.15),
    0 0 0 1px rgba(255, 255, 255, 0.8);
  transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  
  &:hover {
    transform: scale(1.15);
    background: linear-gradient(135deg, #004744 0%, #004744 100%);
    color: white;
    box-shadow: 
      0 12px 35px rgba(0, 71, 68, 0.4),
      0 0 0 1px rgba(255, 255, 255, 0.9);
  }
`;

const ImageIndicators = styled.div`
  position: absolute;
  bottom: 1.5rem;
  width: 100%;
  display: flex;
  justify-content: center;
  gap: 0.75rem;
  z-index: 2;
`;

const ImageIndicator = styled.div<{ active: boolean }>`
  width: ${props => props.active ? '24px' : '12px'};
  height: 12px;
  border-radius: 6px;
  background: ${props => props.active 
    ? 'linear-gradient(135deg, #ffffff 0%, #f7fafc 100%)' 
    : 'rgba(255, 255, 255, 0.6)'};
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  box-shadow: ${props => props.active 
    ? '0 4px 15px rgba(255, 255, 255, 0.5)' 
    : '0 2px 8px rgba(0, 0, 0, 0.2)'};
  
  &:hover {
    background: linear-gradient(135deg, #ffffff 0%, #f7fafc 100%);
    transform: scale(1.2);
  }
`;

const PropertyHeader = styled.div`
  margin-bottom: 2.5rem;
  position: relative;
`;

const ModalTitle = styled.h2`
  font-size: 2.25rem;
  font-weight: 700;
  margin-bottom: 0.75rem;
  color: #1a202c;
  line-height: 1.2;
  background: linear-gradient(135deg, #1a202c 0%, #2d3748 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const LocationPriceContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 1.5rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.5rem;
  }
`;

const ModalLocation = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1.125rem;
  color: #4a5568;
  font-weight: 500;
  
  &::before {
    content: "";
    display: inline-block;
    width: 10px;
    height: 10px;
    background: #004744;
    border-radius: 50%;
  }
`;

const PriceContainer = styled.div`
  text-align: right;
  
  @media (max-width: 768px) {
    text-align: left;
  }
`;

const ModalPrice = styled.div`
  font-size: 2rem;
  font-weight: 700;
  background: linear-gradient(135deg, #004744 0%, #004744 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 0.25rem;
`;

const PriceSubtext = styled.div`
  font-size: 0.875rem;
  color: #718096;
  font-weight: 500;
`;

const DetailsSection = styled.div`
  margin-bottom: 3rem;
`;

const SectionTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: #2d3748;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  &::before {
    content: "";
    width: 4px;
    height: 20px;
    background: linear-gradient(135deg, #004744 0%, #004744 100%);
    border-radius: 2px;
  }
`;

const DetailsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 1.5rem;
  padding: 2rem;
  background: linear-gradient(135deg, rgba(0, 71, 68, 0.05) 0%, rgba(0, 71, 68, 0.03) 100%);
  border-radius: 16px;
  border: 1px solid rgba(0, 71, 68, 0.1);
  margin-bottom: 0.5rem;
`;

const DetailItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 1rem;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.8) 0%, rgba(255, 255, 255, 0.4) 100%);
  border-radius: 12px;
  transition: all 0.3s ease;
  border: 1px solid rgba(255, 255, 255, 0.5);
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.7) 100%);
  }
`;

const DetailIcon = styled.div`
  font-size: 1.5rem;
  margin-bottom: 0.5rem;
  padding: 0.75rem;
  background: linear-gradient(135deg, #004744 0%, #004744 100%);
  color: white;
  border-radius: 50%;
  width: 3rem;
  height: 3rem;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 15px rgba(0, 71, 68, 0.3);
`;

const DetailValue = styled.span`
  font-size: 1.25rem;
  font-weight: 600;
  color: #2d3748;
  margin-bottom: 0.25rem;
`;

const DetailLabel = styled.span`
  font-size: 0.875rem;
  color: #718096;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const DescriptionSection = styled.div`
  margin-bottom: 3rem;
`;

const ModalDescription = styled.div`
  font-size: 1rem;
  line-height: 1.7;
  color: #4a5568;
  padding: 2rem;
  background: linear-gradient(135deg, rgba(247, 250, 252, 0.5) 0%, rgba(237, 242, 247, 0.3) 100%);
  border-radius: 16px;
  border-left: 4px solid #004744;
  white-space: pre-wrap;
  position: relative;
  
  &::before {
    content: '"';
    position: absolute;
    top: 0.5rem;
    left: 1rem;
    font-size: 3rem;
    color: rgba(0, 71, 68, 0.2);
    font-family: Georgia, serif;
  }
`;

const AmenitiesSection = styled.div`
  margin-bottom: 3rem;
`;

const AmenitiesList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;
`;

const Amenity = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 1.25rem;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.8) 0%, rgba(247, 250, 252, 0.6) 100%);
  border-radius: 12px;
  font-size: 0.95rem;
  color: #4a5568;
  font-weight: 500;
  border: 1px solid rgba(0, 71, 68, 0.1);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateX(4px);
    background: linear-gradient(135deg, rgba(0, 71, 68, 0.05) 0%, rgba(0, 71, 68, 0.03) 100%);
    border-color: rgba(0, 71, 68, 0.2);
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08);
  }
  
  &::before {
    content: "✨";
    font-size: 1rem;
    padding: 0.5rem;
    background: linear-gradient(135deg, #004744 0%, #004744 100%);
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 2rem;
    height: 2rem;
  }
`;

const BookingSection = styled.div`
  margin-top: 3rem;
  padding-top: 3rem;
  border-top: 2px solid rgba(0, 71, 68, 0.1);
  background: linear-gradient(135deg, rgba(0, 71, 68, 0.03) 0%, rgba(0, 71, 68, 0.01) 100%);
  border-radius: 16px;
  padding: 2rem;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 60px;
    height: 4px;
    background: linear-gradient(135deg, #004744 0%, #004744 100%);
    border-radius: 2px;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
`;

const NoAccommodations = styled.div`
  text-align: center;
  padding: 3rem;
  color: #666;
  font-size: 1.2rem;
`;

const Accommodations: React.FC = () => {
  const { language } = useLanguage();
  const [accommodations, setAccommodations] = useState<AccommodationType[]>([]);
  const [filteredAccommodations, setFilteredAccommodations] = useState<AccommodationType[]>([]);
  const [selectedAccommodation, setSelectedAccommodation] = useState<AccommodationType | null>(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showMap, setShowMap] = useState(true);
  const [mapCenter, setMapCenter] = useState({ lat: 43.7228, lng: 10.4017 }); // Pisa default
  
  // Use our modal hook
  const { isOpen: isModalOpen, openModal, closeModal } = useModal();
  
  // Updated property type filter
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [bedsFilter, setBedsFilter] = useState<string>('all');
  // const [priceSort, setPriceSort] = useState<string>('default'); // removed price sort

  useEffect(() => {
    const fetchAccommodations = async () => {
      try {
        const data = await getAccommodations();
        
        // Pre-defined locations throughout Italy with precise coordinates
        const predefinedLocations = [
          // Tuscany
          { name: 'Pisa', lat: 43.7228, lng: 10.4017, region: 'Tuscany' },
          { name: 'Florence', lat: 43.7696, lng: 11.2558, region: 'Tuscany' },
          { name: 'Firenze', lat: 43.7696, lng: 11.2558, region: 'Tuscany' },
          { name: 'Siena', lat: 43.3319, lng: 11.3307, region: 'Tuscany' },
          { name: 'Lucca', lat: 43.8429, lng: 10.5027, region: 'Tuscany' },
          { name: 'Livorno', lat: 43.5485, lng: 10.3106, region: 'Tuscany' },
          { name: 'Viareggio', lat: 43.8708, lng: 10.2308, region: 'Tuscany' },
          { name: 'San Gimignano', lat: 43.4677, lng: 11.0425, region: 'Tuscany' },
          { name: 'Volterra', lat: 43.3999, lng: 10.8603, region: 'Tuscany' },
          { name: 'Arezzo', lat: 43.4632, lng: 11.8796, region: 'Tuscany' },
          { name: 'Montepulciano', lat: 43.1008, lng: 11.7891, region: 'Tuscany' },
          { name: 'Cortona', lat: 43.2751, lng: 11.9871, region: 'Tuscany' },
          { name: 'Massa', lat: 44.0366, lng: 10.1411, region: 'Tuscany' },
          { name: 'Carrara', lat: 44.0803, lng: 10.0971, region: 'Tuscany' },
          { name: 'Pietrasanta', lat: 43.9596, lng: 10.2289, region: 'Tuscany' },
          { name: 'Forte dei Marmi', lat: 43.9603, lng: 10.1731, region: 'Tuscany' },
          
          // Liguria
          { name: 'Genova', lat: 44.4056, lng: 8.9463, region: 'Liguria' },
          { name: 'Genoa', lat: 44.4056, lng: 8.9463, region: 'Liguria' },
          { name: 'Cinque Terre', lat: 44.1185, lng: 9.6542, region: 'Liguria' },
          { name: 'Portofino', lat: 44.3019, lng: 9.2095, region: 'Liguria' },
          { name: 'La Spezia', lat: 44.1056, lng: 9.8240, region: 'Liguria' },
          { name: 'Rapallo', lat: 44.3507, lng: 9.2296, region: 'Liguria' },
          { name: 'Santa Margherita Ligure', lat: 44.3342, lng: 9.2107, region: 'Liguria' },
          { name: 'Sanremo', lat: 43.8167, lng: 7.7786, region: 'Liguria' },
          { name: 'Imperia', lat: 43.8879, lng: 8.0276, region: 'Liguria' },
          { name: 'Savona', lat: 44.3090, lng: 8.4810, region: 'Liguria' },
          { name: 'Alassio', lat: 44.0058, lng: 8.1691, region: 'Liguria' },
          { name: 'Lerici', lat: 44.0756, lng: 9.9115, region: 'Liguria' },
          { name: 'Monterosso', lat: 44.1450, lng: 9.6542, region: 'Liguria' },
          { name: 'Vernazza', lat: 44.1353, lng: 9.6810, region: 'Liguria' },
          { name: 'Corniglia', lat: 44.1198, lng: 9.7134, region: 'Liguria' },
          { name: 'Manarola', lat: 44.1108, lng: 9.7293, region: 'Liguria' },
          { name: 'Riomaggiore', lat: 44.1010, lng: 9.7390, region: 'Liguria' },
          
          // Other major Italian cities
          { name: 'Roma', lat: 41.9028, lng: 12.4964, region: 'Lazio' },
          { name: 'Rome', lat: 41.9028, lng: 12.4964, region: 'Lazio' },
          { name: 'Milano', lat: 45.4642, lng: 9.1900, region: 'Lombardy' },
          { name: 'Milan', lat: 45.4642, lng: 9.1900, region: 'Lombardy' },
          { name: 'Venezia', lat: 45.4408, lng: 12.3155, region: 'Veneto' },
          { name: 'Venice', lat: 45.4408, lng: 12.3155, region: 'Veneto' },
          { name: 'Napoli', lat: 40.8518, lng: 14.2681, region: 'Campania' },
          { name: 'Naples', lat: 40.8518, lng: 14.2681, region: 'Campania' },
          { name: 'Bologna', lat: 44.4949, lng: 11.3426, region: 'Emilia-Romagna' },
          { name: 'Torino', lat: 45.0703, lng: 7.6869, region: 'Piedmont' },
          { name: 'Turin', lat: 45.0703, lng: 7.6869, region: 'Piedmont' },
          { name: 'Palermo', lat: 38.1157, lng: 13.3615, region: 'Sicily' },
          { name: 'Bari', lat: 41.1177, lng: 16.8719, region: 'Puglia' },
          { name: 'Catania', lat: 37.5079, lng: 15.0830, region: 'Sicily' },
          { name: 'Verona', lat: 45.4384, lng: 10.9916, region: 'Veneto' },
          { name: 'Messina', lat: 38.1938, lng: 15.5540, region: 'Sicily' },
          { name: 'Padova', lat: 45.4064, lng: 11.8768, region: 'Veneto' },
          { name: 'Trieste', lat: 45.6495, lng: 13.7768, region: 'Friuli-Venezia Giulia' },
          { name: 'Taranto', lat: 40.4624, lng: 17.2468, region: 'Puglia' },
          { name: 'Brescia', lat: 45.5416, lng: 10.2118, region: 'Lombardy' },
          { name: 'Parma', lat: 44.8015, lng: 10.3279, region: 'Emilia-Romagna' },
          { name: 'Reggio Calabria', lat: 38.1060, lng: 15.6467, region: 'Calabria' },
          { name: 'Modena', lat: 44.6471, lng: 10.9252, region: 'Emilia-Romagna' },
          { name: 'Perugia', lat: 43.1122, lng: 12.3888, region: 'Umbria' },
          { name: 'Ravenna', lat: 44.4194, lng: 12.1974, region: 'Emilia-Romagna' },
          { name: 'Rimini', lat: 44.0678, lng: 12.5695, region: 'Emilia-Romagna' },
          { name: 'Salerno', lat: 40.6824, lng: 14.7681, region: 'Campania' },
          { name: 'Ferrara', lat: 44.8379, lng: 11.6203, region: 'Emilia-Romagna' },
          { name: 'Sassari', lat: 40.7259, lng: 8.5565, region: 'Sardinia' },
          { name: 'Cagliari', lat: 39.2238, lng: 9.1217, region: 'Sardinia' },
          { name: 'Pescara', lat: 42.4584, lng: 14.2156, region: 'Abruzzo' },
          { name: 'Monza', lat: 45.5845, lng: 9.2744, region: 'Lombardy' },
          { name: 'Bergamo', lat: 45.6983, lng: 9.6773, region: 'Lombardy' },
          { name: 'Como', lat: 45.8081, lng: 9.0852, region: 'Lombardy' },
          { name: 'Lecce', lat: 40.3515, lng: 18.1750, region: 'Puglia' },
          { name: 'Vicenza', lat: 45.5455, lng: 11.5353, region: 'Veneto' },
          { name: 'Terni', lat: 42.5635, lng: 12.6450, region: 'Umbria' },
          { name: 'Forlì', lat: 44.2226, lng: 12.0401, region: 'Emilia-Romagna' },
          { name: 'Trento', lat: 46.0747, lng: 11.1217, region: 'Trentino-Alto Adige' },
          { name: 'Novara', lat: 45.4469, lng: 8.6210, region: 'Piedmont' },
          { name: 'Piacenza', lat: 45.0526, lng: 9.6934, region: 'Emilia-Romagna' },
          { name: 'Ancona', lat: 43.6158, lng: 13.5189, region: 'Marche' },
          { name: 'Bolzano', lat: 46.4983, lng: 11.3547, region: 'Trentino-Alto Adige' },
          { name: 'Udine', lat: 46.0710, lng: 13.2384, region: 'Friuli-Venezia Giulia' },
          { name: 'Cesena', lat: 44.1391, lng: 12.2431, region: 'Emilia-Romagna' },
          { name: 'Pesaro', lat: 43.9102, lng: 12.9132, region: 'Marche' },
        ];
        
        // Create a map to track how many accommodations are assigned to each location
        const locationCounts = new Map<string, number>();
        
        // Manual coordinate overrides for specific accommodations (if needed)
        const manualCoordinates: { [key: string]: { lat: number; lng: number; name: string } } = {
          // Example: 'accommodation-title-or-id': { lat: 43.7696, lng: 11.2558, name: 'Florence' },
          // Add specific overrides here if certain accommodations need precise coordinates
        };
        
        // Assign real coordinates or use proper predefined locations
        const dataWithCoordinates = data.map((acc) => {
          // First, check for manual coordinate overrides
          const manualCoord = manualCoordinates[acc.id] || 
                             manualCoordinates[acc.title_en?.toLowerCase()] || 
                             manualCoordinates[acc.title_it?.toLowerCase()];
          
          if (manualCoord) {
            console.log(`🎯 Using manual coordinates for "${acc.title_en || acc.title_it}": ${manualCoord.name}`);
            return {
              ...acc,
              latitude: manualCoord.lat,
              longitude: manualCoord.lng,
              _assignedLocation: manualCoord.name
            };
          }
          
          // If accommodation has valid coordinates, use them
          if (acc.latitude && acc.longitude && 
              !isNaN(acc.latitude) && !isNaN(acc.longitude) && 
              acc.latitude !== 0 && acc.longitude !== 0 &&
              acc.latitude >= -90 && acc.latitude <= 90 &&
              acc.longitude >= -180 && acc.longitude <= 180) {
            console.log(`✅ Using original coordinates for "${acc.title_en || acc.title_it}": ${acc.latitude}, ${acc.longitude}`);
            return acc;
          }
          
          // Otherwise, assign a location based on the name
          let assignedLocation;
          
          // Try to match accommodation location with predefined locations (both IT and EN)
          if (acc.location_en || acc.location_it) {
            const locationEN = (acc.location_en || '').toLowerCase();
            const locationIT = (acc.location_it || '').toLowerCase();
            
            assignedLocation = predefinedLocations.find(loc => {
              const locName = loc.name.toLowerCase();
              return locationEN.includes(locName) || 
                     locationIT.includes(locName) ||
                     locName.includes(locationEN) ||
                     locName.includes(locationIT);
            });
          }
          
          // If no match was found, try to match by title
          if (!assignedLocation && (acc.title_en || acc.title_it)) {
            const titleEN = (acc.title_en || '').toLowerCase();
            const titleIT = (acc.title_it || '').toLowerCase();
            
            assignedLocation = predefinedLocations.find(loc => {
              const locName = loc.name.toLowerCase();
              return titleEN.includes(locName) || 
                     titleIT.includes(locName) ||
                     locName.includes(titleEN) ||
                     locName.includes(titleIT);
            });
          }
          
          // If still no match, default to Pisa
          if (!assignedLocation) {
            assignedLocation = predefinedLocations.find(loc => loc.name === 'Pisa') || 
                              (predefinedLocations && predefinedLocations.length > 0 ? predefinedLocations[0] : 
                               { name: 'Pisa', lat: 43.7228, lng: 10.4017, region: 'Tuscany' });
          }
          
          // Create a unique key for this location
          const locationKey = `${assignedLocation.name}`;
          const currentCount = locationCounts.get(locationKey) || 0;
          locationCounts.set(locationKey, currentCount + 1);
          
          // Debug logging to track matching
          console.log(`🏠 Accommodation "${acc.title_en || acc.title_it}" matched to ${assignedLocation.name} (${assignedLocation.region})`);
          if (acc.location_en || acc.location_it) {
            console.log(`   📍 Original location: IT="${acc.location_it}" EN="${acc.location_en}"`);
          }
          
          // For multiple accommodations in the same location, create a small grid pattern
          // instead of random offsets for more predictable positioning
          const gridOffset = 0.003; // ~300 meters
          const gridSize = 3; // 3x3 grid
          const row = Math.floor(currentCount / gridSize);
          const col = currentCount % gridSize;
          
          const offsetLat = (row - Math.floor(gridSize / 2)) * gridOffset;
          const offsetLng = (col - Math.floor(gridSize / 2)) * gridOffset;
          
          console.log(`   🎯 Final coordinates: ${(assignedLocation.lat + offsetLat).toFixed(6)}, ${(assignedLocation.lng + offsetLng).toFixed(6)} (offset: ${currentCount})`);
          
          return {
            ...acc,
            latitude: assignedLocation.lat + offsetLat,
            longitude: assignedLocation.lng + offsetLng,
            _assignedLocation: assignedLocation.name // for debugging
          };
        });
        
        setAccommodations(dataWithCoordinates);
        setFilteredAccommodations(dataWithCoordinates);
        
        // Calculate an optimized map center based on the center of visible accommodations
        if (dataWithCoordinates.length > 0) {
          const validCoordinates = dataWithCoordinates.filter(acc => 
            acc.latitude && acc.longitude && 
            !isNaN(acc.latitude) && !isNaN(acc.longitude)
          );
          
          if (validCoordinates.length > 0) {
            const totalLat = validCoordinates.reduce((sum, acc) => sum + acc.latitude, 0);
            const totalLng = validCoordinates.reduce((sum, acc) => sum + acc.longitude, 0);
            const avgLat = totalLat / validCoordinates.length;
            const avgLng = totalLng / validCoordinates.length;
            setMapCenter({ lat: avgLat, lng: avgLng });
          }
        }
      } catch (error) {
        console.error('Error fetching accommodations:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAccommodations();
  }, []);
  
  useEffect(() => {
    // Apply filters
    let filtered = [...accommodations];
    
    // Filter by type
    if (typeFilter !== 'all') {
      if (typeFilter === 'terrace') {
        filtered = filtered.filter(acc => acc.terrace === true);
      } else {
        filtered = filtered.filter(acc => acc.type === typeFilter);
      }
    }
    
    // Filter by beds
    if (bedsFilter !== 'all') {
      const minBeds = parseInt(bedsFilter);
      filtered = filtered.filter(acc => acc.max_guests >= minBeds);
    }
    
    // Sort by price (this is a simplified example, would need to parse the price strings properly)
    // if (priceSort === 'asc' || priceSort === 'desc') {
    //   filtered.sort((a, b) => {
    //     const priceA = parseInt(a.price_en.replace(/[^0-9]/g, ''));
    //     const priceB = parseInt(b.price_en.replace(/[^0-9]/g, ''));
    //     return priceSort === 'asc' ? priceA - priceB : priceB - priceA;
    //   });
    // }
    
    setFilteredAccommodations(filtered);
  }, [accommodations, typeFilter, bedsFilter]);

  const handleOpenModal = (accommodation: AccommodationType) => {
    setSelectedAccommodation(accommodation);
    // Reset image index when opening modal, but only if there are images
    if (accommodation.images && accommodation.images.length > 0) {
      setCurrentImageIndex(0);
    } else {
      setCurrentImageIndex(0); // Still set to 0 for consistency
    }
    openModal(); // Use our hook to handle modal state
  };
  
  const handleCloseModal = () => {
    setSelectedAccommodation(null);
    closeModal(); // Use our hook to handle modal state
  };
  
  const handlePrevImage = () => {
    if (selectedAccommodation && selectedAccommodation.images && selectedAccommodation.images.length > 0) {
      setCurrentImageIndex((prevIndex) => 
        prevIndex === 0 ? selectedAccommodation.images.length - 1 : prevIndex - 1
      );
    }
  };
  
  const handleNextImage = () => {
    if (selectedAccommodation && selectedAccommodation.images && selectedAccommodation.images.length > 0) {
      setCurrentImageIndex((prevIndex) => 
        prevIndex === selectedAccommodation.images.length - 1 ? 0 : prevIndex + 1
      );
    }
  };
  
  const handleImageIndicatorClick = (index: number) => {
    setCurrentImageIndex(index);
  };
  
  const clearFilters = () => {
    setTypeFilter('all');
    setBedsFilter('all');
    // setPriceSort('default'); // removed price sort
  };
  
  // Get localized content based on the selected language
  const getLocalizedContent = (content: { it: string; en: string }) => {
    return language === 'it' ? content.it : content.en;
  };
  
  // Inside the component, update the map markers definition:
  const mapMarkers: Marker[] = [
    // Markers for filtered accommodations with validation
    ...filteredAccommodations
      .filter(acc => 
        acc.latitude && acc.longitude && 
        !isNaN(acc.latitude) && !isNaN(acc.longitude) &&
        acc.latitude !== 0 && acc.longitude !== 0
      )
      .map(acc => ({
        id: acc.id,
        position: { lat: acc.latitude, lng: acc.longitude },
        title: language === 'it' ? acc.title_it : acc.title_en
      }))
  ];
  
  // Clean up on unmount
  useEffect(() => {
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  return (
    <>
      <SEOHelmet
        title={language === 'it' ? 'Italia - Regione' : 'Italy - Region'}
        description={language === 'it'
          ? 'Scopri la nostra selezione di alloggi di lusso in Italia. Ville con piscina, appartamenti nel centro storico e case al mare.'
          : 'Discover our selection of luxury accommodations in Italy. Villas with pool, apartments in the historic center and beach houses.'}
        keywords={[
          language === 'it' ? 'alloggi lusso italia' : 'luxury accommodation italy',
          language === 'it' ? 'ville italiane' : 'italian villas',
          language === 'it' ? 'appartamenti di prestigio' : 'prestigious apartments'
        ]}
      />

      <HeaderBannerComponent 
        title={language === 'it' ? 'I Nostri Alloggi' : 'Our Accommodations'} 
        imageUrl="https://images.unsplash.com/photo-1566073771259-6a8506099945?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80"
      />

      <Section>
        
        <FiltersContainer>
          <FilterHeader>
            <FilterTitle>{language === 'it' ? 'Filtra gli alloggi' : 'Filter Accommodations'}</FilterTitle>
            <LayoutToggle>
              <LayoutButton 
                active={!showMap} 
                onClick={() => setShowMap(false)}
                title={language === 'it' ? 'Visualizza a griglia' : 'Grid view'}
              >
                <span>□□</span>
              </LayoutButton>
              <LayoutButton 
                active={showMap} 
                onClick={() => setShowMap(true)}
                title={language === 'it' ? 'Visualizza mappa' : 'Map view'}
              >
                <span>▤</span>
              </LayoutButton>
              <ClearFiltersButton onClick={clearFilters}>
                {language === 'it' ? 'Reset' : 'Reset'}
              </ClearFiltersButton>
            </LayoutToggle>
          </FilterHeader>
          
          <FilterGroup>
            <FilterLabel>{language === 'it' ? 'Categoria' : 'Category'}</FilterLabel>
            <FilterButtonsGroup>
              <FilterButton 
                active={typeFilter === 'all'} 
                onClick={() => setTypeFilter('all')}
              >
                {language === 'it' ? 'Tutti' : 'All'}
              </FilterButton>
              <FilterButton 
                active={typeFilter === 'apartment'} 
                onClick={() => setTypeFilter('apartment')}
              >
                {language === 'it' ? 'Appartamenti' : 'Apartments'}
              </FilterButton>
              <FilterButton 
                active={typeFilter === 'attic'} 
                onClick={() => setTypeFilter('attic')}
              >
                {language === 'it' ? 'Attici' : 'Attics'}
              </FilterButton>
              <FilterButton 
                active={typeFilter === 'castle'} 
                onClick={() => setTypeFilter('castle')}
              >
                {language === 'it' ? 'Castelli' : 'Castles'}
              </FilterButton>
              <FilterButton 
                active={typeFilter === 'loft'} 
                onClick={() => setTypeFilter('loft')}
              >
                {language === 'it' ? 'Loft' : 'Lofts'}
              </FilterButton>
              <FilterButton 
                active={typeFilter === 'villa'} 
                onClick={() => setTypeFilter('villa')}
              >
                {language === 'it' ? 'Ville' : 'Villas'}
              </FilterButton>
              <FilterButton 
                active={typeFilter === 'terrace'} 
                onClick={() => setTypeFilter('terrace')}
              >
                {language === 'it' ? 'Terrazze' : 'Terraces'}
              </FilterButton>
            </FilterButtonsGroup>
          </FilterGroup>
          
          <FilterGroup>
            <FilterLabel>{language === 'it' ? 'Numero ospiti' : 'Number of Guests'}</FilterLabel>
            <FilterSelect 
              value={bedsFilter}
              onChange={(e) => setBedsFilter(e.target.value)}
            >
              <option value="all">{language === 'it' ? 'Tutti' : 'All'}</option>
              <option value="1">1+</option>
              <option value="2">2+</option>
              <option value="3">3+</option>
              <option value="4">4+</option>
              <option value="5">5+</option>
            </FilterSelect>
          </FilterGroup>
          {/* Removed price filter group */}
        </FiltersContainer>
        
        {showMap && (
          <InteractiveMap 
            markers={mapMarkers}
            center={mapCenter}
            legendText={language === 'it' ? 'Posizioni alloggi' : 'Accommodation locations'}
            zoom={9}
          />
        )}

        {loading ? (
          <LoadingContainer>
            <Spinner />
          </LoadingContainer>
        ) : filteredAccommodations.length > 0 ? (
          <AccommodationsGrid>
          {filteredAccommodations.map((accommodation) => (
              <div key={accommodation.id}>
              <Card
                image={accommodation.image}
                  title={language === 'it' ? accommodation.title_it : accommodation.title_en}
                  subtitle={language === 'it' ? accommodation.location_it : accommodation.location_en}
                  onClick={() => handleOpenModal(accommodation)}
                  hoverEffect={true}
                />
              </div>
            ))}
          </AccommodationsGrid>
        ) : (
          <NoAccommodations>
            {language === 'it' ? 'Nessun alloggio disponibile con i filtri selezionati.' : 'No accommodations available with the selected filters.'}
          </NoAccommodations>
        )}
      </Section>

      {selectedAccommodation && (
        <ModalOverlay onClick={handleCloseModal}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <CloseButton onClick={handleCloseModal}>×</CloseButton>
            <ImageContainer>
              <Image 
                src={
                  (selectedAccommodation.images && selectedAccommodation.images.length > 0) 
                    ? selectedAccommodation.images[currentImageIndex] || selectedAccommodation.image
                    : selectedAccommodation.image
                } 
                alt={language === 'it' ? selectedAccommodation.title_it : selectedAccommodation.title_en} 
              />
              {selectedAccommodation.images && selectedAccommodation.images.length > 1 && (
                <>
                  <ImageNav>
                    <NavButton onClick={handlePrevImage}>←</NavButton>
                    <NavButton onClick={handleNextImage}>→</NavButton>
                  </ImageNav>
                  <ImageIndicators>
                    {selectedAccommodation.images.map((_, index) => (
                      <ImageIndicator 
                        key={index} 
                        active={index === currentImageIndex} 
                        onClick={() => handleImageIndicatorClick(index)} 
                      />
                    ))}
                  </ImageIndicators>
                </>
              )}
            </ImageContainer>
              <ModalBody>
              <PropertyHeader>
                <ModalTitle>{language === 'it' ? selectedAccommodation.title_it : selectedAccommodation.title_en}</ModalTitle>
                <LocationPriceContainer>
                  <ModalLocation>{language === 'it' ? selectedAccommodation.location_it : selectedAccommodation.location_en}</ModalLocation>
                  {/* Removed price display from modal */}
                </LocationPriceContainer>
              </PropertyHeader>
              
              <DetailsSection>
                <SectionTitle>{language === 'it' ? 'Dettagli' : 'Details'}</SectionTitle>
                <DetailsGrid>
                  <DetailItem>
                    <DetailIcon>👥</DetailIcon>
                    <DetailValue>{selectedAccommodation.max_guests}</DetailValue>
                    <DetailLabel>{language === 'it' ? 'Numero ospiti' : 'Number of Guests'}</DetailLabel>
                  </DetailItem>
                  <DetailItem>
                    <DetailIcon>🛁</DetailIcon>
                    <DetailValue>{selectedAccommodation.bathrooms}</DetailValue>
                    <DetailLabel>{language === 'it' ? 'Bagni' : 'Bathrooms'}</DetailLabel>
                  </DetailItem>
                  <DetailItem>
                    <DetailIcon>📏</DetailIcon>
                    <DetailValue>{selectedAccommodation.size}</DetailValue>
                    <DetailLabel>{language === 'it' ? 'Dimensione' : 'Size'}</DetailLabel>
                  </DetailItem>
                  <DetailItem>
                    <DetailIcon>🏠</DetailIcon>
                    <DetailValue style={{ textTransform: 'capitalize' }}>
                      {selectedAccommodation.type === 'apartment' 
                        ? (language === 'it' ? 'Appartamento' : 'Apartment')
                        : selectedAccommodation.type === 'villa'
                        ? (language === 'it' ? 'Villa' : 'Villa')
                        : selectedAccommodation.type === 'house'
                        ? (language === 'it' ? 'Casa' : 'House')
                        : (language === 'it' ? 'Studio' : 'Studio')
                      }
                    </DetailValue>
                    <DetailLabel>{language === 'it' ? 'Tipo' : 'Type'}</DetailLabel>
                  </DetailItem>
                  {selectedAccommodation.terrace && (
                    <DetailItem>
                      <DetailIcon>🌞</DetailIcon>
                      <DetailValue>
                        <span style={{ color: '#004744' }}>✓</span>
                      </DetailValue>
                      <DetailLabel>{language === 'it' ? 'Terrazza' : 'Terrace'}</DetailLabel>
                    </DetailItem>
                  )}
                </DetailsGrid>
              </DetailsSection>
              
              <DescriptionSection>
                <SectionTitle>{language === 'it' ? 'Descrizione' : 'Description'}</SectionTitle>
                <ModalDescription>
                  {language === 'it' ? selectedAccommodation.description_it : selectedAccommodation.description_en}
                </ModalDescription>
              </DescriptionSection>
              
              <AmenitiesSection>
                <SectionTitle>{language === 'it' ? 'Servizi' : 'Amenities'}</SectionTitle>
                <AmenitiesList>
                  {selectedAccommodation.amenities && selectedAccommodation.amenities.length > 0 
                    ? selectedAccommodation.amenities.map((amenity, index) => (
                        <Amenity key={index}>
                          {language === 'it' ? amenity.it : amenity.en}
                        </Amenity>
                      ))
                    : <Amenity>{language === 'it' ? 'Nessun servizio disponibile' : 'No amenities available'}</Amenity>
                  }
                </AmenitiesList>
              </AmenitiesSection>

              {/* Booking Form */}
              <BookingSection>
                <BookingForm
                  accommodationId={selectedAccommodation.id}
                  accommodationTitle={language === 'it' ? selectedAccommodation.title_it : selectedAccommodation.title_en}
                  basePrice={parseInt((language === 'it' ? selectedAccommodation.price_it : selectedAccommodation.price_en).replace(/[^0-9]/g, '')) || 100}
                  maxGuests={selectedAccommodation.max_guests || 2}
                  airbnbListingId={(selectedAccommodation as any).airbnb_listing_id || null}
                />
              </BookingSection>
              </ModalBody>
            </ModalContent>
        </ModalOverlay>
        )}
    </>
  );
};

export default Accommodations; 